import { OfflineSyncType } from '@/utils/offline-mode';
import {
  OfflineUserBackgroundSyncRegistryAdapter,
  OfflineUserContextAdapter,
} from '@/adapters/offline-mode';
import { partnerSupplier } from '@/utils/environment';
import { print as printQuery } from 'graphql';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { USER_QUERY } from '@/contexts/user-context-queries';
import {
  UserContext_EmployeeQuery,
  UserContext_EmployeeQueryVariables,
} from '@/types';

/**
 * Custom hook to download and save the user context for the active user.
 *
 * This hook uses a lazy query to fetch the user context from the server and then saves it
 * locally using the OfflineUserContextAdapter.
 *
 * @returns {Object} - An object containing the downloadUserContext function.
 *
 * @function
 * @name useDownloadUserContext
 *
 * @example
 * const { downloadUserContext } = useDownloadUserContext();
 *
 * // Download and save the user context for the active user
 * downloadUserContext();
 */
export const useDownloadUserContext = () => {
  const { user } = useAuthenticatedUserContext();
  const getUserContext = useLazyQuery<
    UserContext_EmployeeQuery,
    UserContext_EmployeeQueryVariables
  >(USER_QUERY, {
    fetchPolicy: 'network-only',
  });

  /**
   * Fetches and saves the user context for the active user.
   */
  const downloadUserContext = async () => {
    const variables = {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      employeeChatsFilter: {
        business_organization_uuid: {
          _eq: partnerSupplier.businessOrganizationUUID,
        },
      },
      userUUID: user.uuid,
    };

    const { data } = await getUserContext({
      context: {
        offlineSync: true,
      },
      variables,
    });
    const offlineUserContextAdapter = new OfflineUserContextAdapter(user.uuid);
    await offlineUserContextAdapter.save(data);

    const userBackgroundSyncRegistryAdapter =
      new OfflineUserBackgroundSyncRegistryAdapter();
    await userBackgroundSyncRegistryAdapter.updateUserSyncMetadata(user.uuid, {
      query: printQuery(USER_QUERY),
      syncType: OfflineSyncType.UserContext,
      variables,
    });
  };

  return { downloadUserContext };
};
